/**
 * GovernanceSlot — Cross-Platform Feature Flag Component
 *
 * Conditionally renders cross-platform data when BSU and LSU are connected.
 * When standalone (not connected), renders nothing — no placeholders,
 * no "connect to see more" prompts, no empty slots.
 *
 * Usage:
 * ```tsx
 * <ChannelCard channel={channel}>
 *   <GovernanceSlot slot="legal-status">
 *     <LegalStatusBadge entityId={channel.entityId} />
 *   </GovernanceSlot>
 * </ChannelCard>
 * ```
 *
 * Convoy-045 — SuiteShell Unified UI/UX
 */

import React, { createContext, useContext, type ReactNode } from 'react';

// ─── Context ──────────────────────────────────────────────────

interface GovernanceContextValue {
  /** Whether cross-platform governance is connected */
  isConnected: boolean;
  /** Which platforms are connected */
  platforms: Array<'bsu' | 'lsu'>;
}

const GovernanceContext = createContext<GovernanceContextValue>({
  isConnected: false,
  platforms: [],
});

// eslint-disable-next-line react-refresh/only-export-components
export function useGovernanceContext() {
  return useContext(GovernanceContext);
}

// ─── Provider ─────────────────────────────────────────────────

interface GovernanceProviderProps {
  children: ReactNode;
  /** Whether governance is connected. Defaults to VITE_GOVERNANCE_CONNECTED env var. */
  connected?: boolean;
  /** Connected platforms */
  platforms?: Array<'bsu' | 'lsu'>;
}

export function GovernanceProvider({
  children,
  connected,
  platforms = [],
}: GovernanceProviderProps) {
  const isConnected = connected ?? import.meta.env.VITE_GOVERNANCE_CONNECTED === 'true';

  return (
    <GovernanceContext.Provider value={{ isConnected, platforms }}>
      {children}
    </GovernanceContext.Provider>
  );
}

// ─── Slot Component ───────────────────────────────────────────

interface GovernanceSlotProps {
  /** Slot identifier for debugging/tracking */
  slot: string;
  /** Content to render when governance is connected */
  children: ReactNode;
  /** Optional: only render when a specific platform is connected */
  requirePlatform?: 'bsu' | 'lsu';
}

/**
 * GovernanceSlot renders its children only when governance is connected.
 * Returns null otherwise — no visual trace in the DOM.
 */
export function GovernanceSlot({ slot, children, requirePlatform }: GovernanceSlotProps) {
  const { isConnected, platforms } = useGovernanceContext();

  if (!isConnected) return null;
  if (requirePlatform && !platforms.includes(requirePlatform)) return null;

  return <div data-governance-slot={slot}>{children}</div>;
}

export default GovernanceSlot;
